import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Container } from 'semantic-ui-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'semantic-ui-css/semantic.min.css';
import './styles/variables.css';
import './index.css';

// Contextos
import { AuthProvider, useAuth } from './context/AuthContext';
import { SimulationToContractProvider } from './context/SimulationToContractContext';
import { QuizProvider } from './context/QuizContext';

// Componentes
import Header from './components/Layout/Header';
import Sidebar from './components/Layout/Sidebar';
import LoginPage from './pages/Auth/LoginPage';
import AccessRestrictedPage from './pages/Auth/AccessRestrictedPage';
import AccessManagementPage from './pages/AccessManagement/AccessManagementPage';
import AuthorizedDevicesPage from './pages/AuthorizedDevices/AuthorizedDevicesPage';
import DeviceUserExemptionsPage from './pages/AuthorizedDevices/DeviceUserExemptionsPage';
import DashboardPage from './pages/Dashboard/DashboardPage';
import UsersPage from './pages/Users/UsersPage';
import ClientsPage from './pages/Clients/ClientsPage';
import ContractsPage from './pages/Contracts/ContractsPage';
import CqvViewPage from './pages/Contracts/CqvViewPage';
import ContractExcelImportPage from './pages/Contracts/ContractExcelImportPage';
import ContractOperationsPage from './pages/Contracts/ContractOperationsPage';
import ClientContractHubPage from './pages/Contracts/ClientContractHubPage';
import PlansPage from './pages/Plans/PlansPage';
import HandsetDevicesPage from './pages/HandsetDevices/HandsetDevicesPage';
import ActivitiesPage from './pages/Activities/ActivitiesPage';
import RolesPage from './pages/Roles/RolesPage';
import ICcIdsPage from './pages/ICcIds/ICcIdsPage';
import ConversationsPage from './pages/Conversations/ConversationsPage';
import ClientsImportPage from './pages/WhatsApp/ClientsImportPage';
import PoliticaPrivacidadePage from './pages/Policy/PoliticaPrivacidadePage';
import DashboardWhatsAppPage from './pages/Dashboard/WhatsAppDashboard/DashboardWhatsAppPage';
import TeamsPage from './pages/Teams/TeamsPage';
import CqvVtmeAssignmentsPage from './pages/Cqv/CqvVtmeAssignmentsPage';
import FilasVtmePage from './pages/FilasVtme/FilasVtmePage';
import PosVendaEmailsPage from './pages/PosVenda/PosVendaEmailsPage';
import PosVendaEnviosPage from './pages/PosVenda/PosVendaEnviosPage';
import PosVendaConfirmacaoEnderecoPage from './pages/PosVenda/PosVendaConfirmacaoEnderecoPage';
import PosVendaPage from './pages/PosVenda/PosVendaPage';
import PosVendaCancelamentosDoDiaPage from './pages/PosVenda/PosVendaCancelamentosDoDiaPage';
import PosVendaRoute from './components/Auth/PosVendaRoute';
import ColetaLoginsPage from './pages/Coleta/ColetaLoginsPage';
import ColetaOperacaoPage from './pages/Coleta/ColetaOperacaoPage';
import CqvSchedulePage from './pages/Cqv/CqvSchedulePage';
import CampaignsPage from './pages/Campaigns/CampaignsPage';
import CampaignDashboardPage from './pages/Campaigns/CampaignDashboardPage';
// import CampaignComparisonPage from './pages/Campaigns/CampaignComparisonPage'; // Módulo desativado temporariamente
import PerformancePage from './pages/Campaigns/PerformancePage';
import SimulationPage from './pages/Simulation/SimulationPage';
import SuspensionsPage from './pages/Suspensions/SuspensionsPage';
import SuspensionsDueTodayPage from './pages/Suspensions/SuspensionsDueTodayPage';
import LearningPage from './pages/Learning/LearningPage';
import QuizTakingPage from './pages/Learning/Quizzes/QuizTakingPage';
import QuizResultPage from './pages/Learning/Quizzes/QuizResultPage';
import QuizReviewPage from './pages/Learning/Quizzes/QuizReviewPage';
import UserQuizAttemptsPage from './pages/Learning/Quizzes/UserQuizAttemptsPage';
import QuizManagementPage from './pages/Learning/Quizzes/QuizManagementPage';
import QuizFormPage from './pages/Learning/Quizzes/QuizFormPage';
import QuizReportsPage from './pages/Learning/Reports/QuizReportsPage';
import TrainingDashboardPage from './pages/Training/TrainingDashboardPage';

// WhatsApp Pages
import WhatsAppBatchesPage from './pages/WhatsApp/BatchesPage';
import WhatsAppClientsPage from './pages/WhatsApp/ClientsPage';
import WhatsAppTemplatesPage from './pages/WhatsApp/TemplatesPage';
import WhatsAppAttendantsPage from './pages/WhatsApp/AttendantsPage';

// Componente para rotas protegidas
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { getDefaultHomePath } from './utils/homeRedirect';

// Simulation Components
import FloatingSimulationButton from './components/Simulation/FloatingSimulationButton';
import FloatingFabCluster, {
  FAB_CLUSTER_SCROLL_RESERVE_PX,
} from './components/Layout/FloatingFabCluster';
// TIM Data Components
import FloatingTimDataButton from './components/TimData/FloatingTimDataButton';
import FloatingPosVendaTickets from './components/posVenda/FloatingPosVendaTickets';

const DefaultHomeRedirect: React.FC = () => {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  return <Navigate to={getDefaultHomePath(user?.roles ?? [])} replace />;
};

// Layout principal
const PageContentWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isFullViewportPage =
    location.pathname.startsWith('/campaigns') ||
    location.pathname.startsWith('/contracts/excel-import');

  // Ler estado do sidebar do localStorage
  const [sidebarWidth, setSidebarWidth] = React.useState<string>(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    const isCollapsed = saved ? JSON.parse(saved) : false;
    return isCollapsed ? '60px' : '200px';
  });

  // Escutar mudanças no sidebar
  React.useEffect(() => {
    const handleSidebarToggle = (e: any) => {
      const isCollapsed = e.detail?.isCollapsed ?? false;
      setSidebarWidth(isCollapsed ? '60px' : '200px');
    };

    window.addEventListener('sidebarToggle', handleSidebarToggle);

    return () => {
      window.removeEventListener('sidebarToggle', handleSidebarToggle);
    };
  }, []);

  return (
    <div
      style={{
        marginLeft: sidebarWidth,
        paddingTop: '47px',
        paddingBottom: isFullViewportPage
          ? 0
          : `calc(${FAB_CLUSTER_SCROLL_RESERVE_PX}px + env(safe-area-inset-bottom, 0px))`,
        flexGrow: 1,
        boxSizing: 'border-box',
        minHeight: isFullViewportPage ? '100vh' : 'calc(100vh - 47px)',
        overflowY: 'auto',
        transition: 'margin-left 0.3s ease',
        background: '#eef1f8',
      }}
    >
      {isFullViewportPage ? children : <Container fluid>{children}</Container>}
    </div>
  );
};

// Componente que controla a visibilidade do layout baseado na autenticação
const LayoutController: React.FC = () => {
  const { isAuthenticated } = useAuth();
  return (
    <>
      {isAuthenticated && <Header />}
      {isAuthenticated && <Sidebar />}
      {isAuthenticated && (
        <>
          <FloatingFabCluster>
            <FloatingSimulationButton embedded />
            <FloatingTimDataButton embedded />
          </FloatingFabCluster>
          <FloatingPosVendaTickets />
        </>
      )}
    </>
  );
};

const CampaignsIndexRedirect: React.FC = () => {
  const { hasRole } = useAuth();
  if (hasRole('TeamLeader') && !hasRole('Admin')) {
    return <Navigate to="/campaigns/my-performance" replace />;
  }
  return <Navigate to="/campaigns/dashboard" replace />;
};

const resolvePageTitle = (pathname: string): string => {
  if (pathname.startsWith('/dashboard/whatsapp')) return 'Dashboard WhatsApp';
  if (pathname.startsWith('/dashboard')) return 'Dashboard';
  if (pathname.startsWith('/clients')) return 'Clientes';
  if (pathname.startsWith('/contracts')) return 'Contratos';
  if (pathname.startsWith('/plans')) return 'Planos';
  if (pathname.startsWith('/users')) return 'Usuarios';
  if (pathname.startsWith('/teams')) return 'Times';
  if (pathname.startsWith('/cqv/atribuicoes-vtme')) return 'Atribuicoes VTME';
  if (pathname.startsWith('/filas-vtme') || pathname.startsWith('/cqv/sla-bko-input')) return 'Filas VTME';
  if (pathname.startsWith('/activities')) return 'Atividades';
  if (pathname.startsWith('/roles')) return 'Cargos';
  if (pathname.startsWith('/iccids')) return 'ICCIDs';
  if (pathname.startsWith('/campaigns/dashboard')) return 'Dashboard de Campanhas';
  if (pathname.startsWith('/campaigns/comparison')) return 'Comparativo de Campanhas';
  if (pathname.startsWith('/campaigns')) return 'Campanhas';
  if (pathname.startsWith('/suspensions/due-today')) return 'Suspensoes Hoje';
  if (pathname.startsWith('/suspensions')) return 'Suspensoes';
  if (pathname.startsWith('/learning')) return 'Centro de Aprendizado';
  if (pathname.startsWith('/training/dashboard')) return 'Dashboard de Treinamento';
  if (pathname.startsWith('/simulation')) return 'Simulacao';
  if (pathname.startsWith('/whatsapp/templates')) return 'Templates WhatsApp';
  if (pathname.startsWith('/whatsapp/attendants')) return 'Atendentes WhatsApp';
  if (pathname.startsWith('/whatsapp/batches')) return 'Lotes WhatsApp';
  if (pathname.startsWith('/whatsapp/clients')) return 'Clientes WhatsApp';
  if (pathname.startsWith('/conversations')) return 'Conversas';
  if (pathname.startsWith('/access-management')) return 'Gerenciamento de Acesso';
  if (pathname.startsWith('/authorized-devices/exemptions')) return 'Liberação por usuário';
  if (pathname.startsWith('/authorized-devices')) return 'Computadores autorizados';
  if (pathname.startsWith('/login')) return 'Login';
  if (pathname.startsWith('/politica-privacidade')) return 'Politica de Privacidade';
  return 'GettelWeb';
};

const RouteTitleManager: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const pageTitle = resolvePageTitle(location.pathname);
    document.title = pageTitle === 'GettelWeb' ? pageTitle : `${pageTitle} - GettelWeb`;
  }, [location.pathname]);

  return null;
};

const App: React.FC = () => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SimulationToContractProvider>
          <QuizProvider>
            <Router>
        <RouteTitleManager />
        <Routes>
          {/* Rota de login - não protegida */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/access-restricted" element={<AccessRestrictedPage />} />

          {/* Rota de política de privacidade - não protegida */}
          <Route path="/politica-privacidade" element={<PoliticaPrivacidadePage />} />

          {/* Rota padrão - redireciona conforme role do usuário */}
          <Route path="/" element={<DefaultHomeRedirect />} />

          {/* Rotas protegidas */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <LayoutController />
                <PageContentWrapper>
                  <DashboardPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/whatsapp"
            element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager']}>
                <LayoutController />
                <PageContentWrapper>
                  <DashboardWhatsAppPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/users/*"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <UsersPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/clients/*"
            element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager', 'Seller', 'TeamLeader']}>
                <LayoutController />
                <PageContentWrapper>
                  <ClientsPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/contracts/excel-import"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <ContractExcelImportPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/contracts/:id/cqv-view"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <CqvViewPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/contracts/operations"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <ContractOperationsPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/contracts/client-hub"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <ClientContractHubPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/contracts/*"
            element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager', 'User', 'Seller', 'BKO', 'Tester', 'TeamLeader']}>
                <LayoutController />
                <PageContentWrapper>
                  <ContractsPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/plans/*"
            element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager', 'Seller', 'TeamLeader']}>
                <LayoutController />
                <PageContentWrapper>
                  <PlansPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/aparelhos/*"
            element={
              <ProtectedRoute requiredRoles={['Admin', 'TeamLeader']}>
                <LayoutController />
                <PageContentWrapper>
                  <HandsetDevicesPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          {/* ✅ ROTA: CENTRO DE APRENDIZADO - Deve estar antes de outras rotas para evitar conflitos */}
          <Route
            path="/learning"
            element={
              <ProtectedRoute>
                <LayoutController />
                <PageContentWrapper>
                  <LearningPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          {/* Rotas de Questionários */}
          <Route
            path="/learning/quizzes/:quizId/take"
            element={
              <ProtectedRoute>
                <LayoutController />
                <PageContentWrapper>
                  <QuizTakingPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/learning/quizzes/participants/:userId/attempts"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <UserQuizAttemptsPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/learning/quizzes/:quizId/review/:attemptId"
            element={
              <ProtectedRoute>
                <LayoutController />
                <PageContentWrapper>
                  <QuizReviewPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/learning/quizzes/:quizId/result"
            element={
              <ProtectedRoute>
                <LayoutController />
                <PageContentWrapper>
                  <QuizResultPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/learning/quizzes/manage"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <QuizManagementPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/learning/quizzes/new"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <QuizFormPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/learning/quizzes/:quizId/edit"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <QuizFormPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          {/* Rotas de Turmas de Treinamento */}

          {/* Rotas de Relatórios */}
          <Route
            path="/learning/reports"
            element={
              <ProtectedRoute requiredRoles={['Admin', 'Supervisor']}>
                <LayoutController />
                <PageContentWrapper>
                  <QuizReportsPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          {/* Dashboard de Treinamento */}
          <Route
            path="/training/dashboard"
            element={
              <ProtectedRoute requiredRoles={['Admin', 'Supervisor']}>
                <LayoutController />
                <PageContentWrapper>
                  <TrainingDashboardPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/activities/*"
            element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager', 'Seller']}>
                <LayoutController />
                <PageContentWrapper>
                  <ActivitiesPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/roles/*"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <RolesPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/teams/*"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <TeamsPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/cqv/atribuicoes-vtme"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <CqvVtmeAssignmentsPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/filas-vtme"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <FilasVtmePage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route path="/cqv/sla-bko-input" element={<Navigate to="/filas-vtme" replace />} />

          <Route
            path="/coleta/logins"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <ColetaLoginsPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/coleta/operacao"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <ColetaOperacaoPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/pos-venda"
            element={
              <ProtectedRoute>
                <PosVendaRoute>
                  <LayoutController />
                  <PageContentWrapper>
                    <PosVendaPage />
                  </PageContentWrapper>
                </PosVendaRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/pos-venda/envios"
            element={
              <ProtectedRoute>
                <PosVendaRoute>
                  <LayoutController />
                  <PageContentWrapper>
                    <PosVendaEnviosPage />
                  </PageContentWrapper>
                </PosVendaRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/pos-venda/confirmacao-endereco"
            element={
              <ProtectedRoute>
                <PosVendaRoute>
                  <LayoutController />
                  <PageContentWrapper>
                    <PosVendaConfirmacaoEnderecoPage />
                  </PageContentWrapper>
                </PosVendaRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/pos-venda/emails"
            element={
              <ProtectedRoute>
                <PosVendaRoute>
                  <LayoutController />
                  <PageContentWrapper>
                    <PosVendaEmailsPage />
                  </PageContentWrapper>
                </PosVendaRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/pos-venda/cancelamentos-do-dia"
            element={
              <ProtectedRoute>
                <PosVendaRoute>
                  <LayoutController />
                  <PageContentWrapper>
                    <PosVendaCancelamentosDoDiaPage />
                  </PageContentWrapper>
                </PosVendaRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/cqv/escala"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <CqvSchedulePage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          {/* Rotas de Campanhas */}
          <Route
            path="/campaigns"
            element={
              <ProtectedRoute requiredRoles={['Admin', 'TeamLeader']}>
                <CampaignsIndexRedirect />
              </ProtectedRoute>
            }
          />

          <Route
            path="/campaigns/dashboard"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <CampaignDashboardPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/campaigns/manage"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <CampaignsPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          {/* Módulo Comparar Campanhas desativado temporariamente
          <Route
            path="/campaigns/compare"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <CampaignComparisonPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />
          */}
          <Route
            path="/campaigns/compare"
            element={<Navigate to="/campaigns" replace />}
          />

          <Route
            path="/campaigns/my-performance"
            element={
              <ProtectedRoute requiredRoles={['Seller', 'TeamLeader']}>
                <LayoutController />
                <PageContentWrapper>
                  <PerformancePage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/icCids/*"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <ICcIdsPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          {/* ✅ NOVA ROTA: SIMULAÇÃO */}
          <Route
            path="/simulation"
            element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager', 'Seller', 'TeamLeader']}>
                <LayoutController />
                <PageContentWrapper>
                  <SimulationPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          {/* ✅ NOVA ROTA: SUSPENSÕES */}
          <Route
            path="/suspensions"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <SuspensionsPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/suspensions/dia"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <SuspensionsDueTodayPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          {/* ✅ NOVA ROTA: CONVERSATIONS (WhatsApp) */}
          <Route
            path="/conversations/*"
            element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager', 'Seller']}>
                <LayoutController />
                <PageContentWrapper>
                  <ConversationsPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          {/* ✅ ROTAS WHATSAPP */}
          <Route
            path="/whatsapp/dashboard"
            element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager']}>
                <LayoutController />
                <PageContentWrapper>
                  <DashboardWhatsAppPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/whatsapp/batches/*"
            element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager', 'Seller']}>
                <LayoutController />
                <PageContentWrapper>
                  <WhatsAppBatchesPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/whatsapp/clients/*"
            element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager']}>
                <LayoutController />
                <PageContentWrapper>
                  <WhatsAppClientsPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/whatsapp/templates/*"
            element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager']}>
                <LayoutController />
                <PageContentWrapper>
                  <WhatsAppTemplatesPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/whatsapp/attendants/*"
            element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager', 'Seller']}>
                <LayoutController />
                <PageContentWrapper>
                  <WhatsAppAttendantsPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/access-management"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <AccessManagementPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/authorized-devices/exemptions"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <DeviceUserExemptionsPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/authorized-devices"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LayoutController />
                <PageContentWrapper>
                  <AuthorizedDevicesPage />
                </PageContentWrapper>
              </ProtectedRoute>
            }
          />

          {/* Rota 404 */}
          <Route path="*" element={<DefaultHomeRedirect />} />
        </Routes>

        {createPortal(
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
            style={{ zIndex: 10020 }}
          />,
          document.body
        )}

            </Router>
          </QuizProvider>
        </SimulationToContractProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
